import { Injectable } from '@angular/core';
import * as Color from 'color';
import { ThemeStore } from 'stores/theme.store';
import { SerializationUtil } from 'utility/serialization.util';
import { ILocationTheme } from '../models/location-theme';
import { RemoteLoggerService } from './remote-logger.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private themeObj?: ILocationTheme;
  private currentTheme?: ILocationTheme;
  private sheet?: CSSStyleSheet;

  constructor(
    private themeStore: ThemeStore,
    private logger: RemoteLoggerService
  ) {}

  setupTheme(themeObj: ILocationTheme) {
    if (!this.currentTheme) {
      this.themeStore.initialTheme =
        SerializationUtil.toSafeJSONObject(themeObj);
    }
    this.themeObj = themeObj;
    this.currentTheme = themeObj;

    try {
      this.injectStyles();
    } catch (e) {
      this.logger.capture(e);
    }
  }

  registerMenuTheme(theme: ILocationTheme) {
    this.themeStore.setMenuTheme({
      accentBackgroundColor: theme.colors?.['accent-background'] ?? '',
      accentTextColor: theme.colors?.['accent-text'] ?? '',
      backgroundColor: theme.colors?.['menu-background'] ?? '',
      textColor: theme.colors?.['menu-text'] ?? '',
      imageBorderStyle:
        theme.images?.['image-styling'] === 'squareCorners'
          ? 'square'
          : 'rounded'
    });
  }

  private injectStyles() {
    let orgTheme = document.querySelector('#theme');
    if (orgTheme) {
      orgTheme.remove();
    }
    let themeStyle = document.createElement('style');

    let themeStyleEl = document.head.appendChild(themeStyle);
    themeStyleEl.id = 'theme';
    this.sheet = themeStyleEl.sheet as CSSStyleSheet;

    try {
      this.injectColors(this.sheet);
    } catch (e) {
      this.logger.capture(e);
    }

    try {
      this.injectImages(this.sheet);
    } catch (e) {
      this.logger.capture(e);
    }

    try {
      this.injectFonts(this.sheet);
    } catch (e) {
      this.injectFallbackFonts(this.sheet);
    }
  }

  private injectColors(sheet: CSSStyleSheet) {
    // @ts-ignore
    let cssRulesKey = Object.keys(this.currentTheme?.colors);
    for (let rule of cssRulesKey) {
      let color: Color;
      try {
        // @ts-ignore
        color = Color(this.currentTheme?.colors[rule].toUpperCase());
      } catch {
        console.warn(`invalid color rule detected: ${rule}`);
        continue;
      }

      if (rule === 'color-primary') {
        this.themeStore.primaryColor = color.hex();
        this.insertProminentColorRule(sheet, color, 'primary');
      } else if (rule === 'color-secondary') {
        this.themeStore.secondaryColor = color.hex();
        this.insertProminentColorRule(sheet, color, 'secondary');
      } else {
        sheet.insertRule(
          // @ts-ignore
          `:root {--${rule}: ${this.currentTheme?.colors[rule]}}`
        );
        sheet.insertRule(`:root {--${rule}-10: ${color.fade(0.9)}}`);
      }
    }
  }

  private insertProminentColorRule(
    sheet: CSSStyleSheet,
    color: Color,
    colorType: 'primary' | 'secondary'
  ) {
    const rgbString = color.rgb().array().join(',');
    sheet.insertRule(
      `:root {
        --v2-color-${colorType}: ${color.hex()};
        --v2-color-${colorType}-rgb: ${rgbString};
        --v2-color-${colorType}-tint: ${color
        .hsv(color.hsv().hue(), 3, 98)
        .hex()};
        
        --ion-color-${colorType}: ${color.hex()};
        --ion-color-${colorType}-rgb: ${rgbString};
        --ion-color-${colorType}-contrast: ${
        color.isDark() ? '#ffffff' : '#000000'
      };
        --ion-color-${colorType}-contrast-rgb: ${
        color.isDark() ? '255,255,255' : '0,0,0'
      };
        --ion-color-${colorType}-shade: ${color.darken(0.1).hex()};
        --ion-color-${colorType}-tint: ${color.lighten(0.1).hex()};
      }`
    );
  }

  private injectImages(sheet: CSSStyleSheet) {
    // @ts-ignore
    let cssRulesKey = Object.keys(this.currentTheme?.images);
    for (let rule of cssRulesKey) {
      // @ts-ignore
      sheet.insertRule(`:root {--${rule}: ${this.currentTheme?.images[rule]}}`);
    }
  }

  private injectFonts(sheet: CSSStyleSheet) {
    let fonts = this.currentTheme?.fonts;
    if (!fonts) return;

    let cssRulesKey = Object.keys(fonts);

    sheet.insertRule(`:root {font-size: ${fonts['body']?.size} !important;}`);

    sheet.insertRule(`ion-app {font-size: ${fonts['body']?.size} !important;}`);

    for (let rule of cssRulesKey) {
      // @ts-ignore
      sheet.insertRule(`:root {--font-${rule}-name: ${fonts[rule].name}}`);
      // @ts-ignore
      sheet.insertRule(`:root {--font-${rule}-url: ${fonts[rule].url}}`);
      // @ts-ignore
      sheet.insertRule(`:root {--font-${rule}-size: ${fonts[rule].size}}`);

      if (
        // @ts-ignore
        (fonts[rule].provider &&
          // @ts-ignore
          fonts[rule].provider.toLowerCase() === 'google') ||
        // @ts-ignore
        !fonts[rule].provider
      ) {
        // @ts-ignore
        this.injectGoogleFonts(sheet, rule, fonts[rule]);
      } else if (
        // @ts-ignore
        fonts[rule].provider &&
        // @ts-ignore
        fonts[rule].provider.toLowerCase() === 'custom'
      ) {
        // @ts-ignore
        this.injectCustomFonts(sheet, rule, fonts[rule]);
      }
    }
  }

  private injectCustomFonts(
    sheet: CSSStyleSheet,
    rule: string,
    font: { providerData: { src: any[] } }
  ) {
    font.providerData.src
      .map((source) => source.descriptors.weight.trim())
      .map((weight) => {
        sheet.insertRule(`:root {--font-${rule}-${weight}-weight: ${weight}}`);
      });
  }

  private injectGoogleFonts(
    sheet: CSSStyleSheet,
    rule: string,
    font: { providerData: { weights: any }; weights: string }
  ) {
    let weights = font.providerData
      ? font.providerData.weights
      : font.weights.split(',');
    weights
      .map((weightType: string) => weightType.trim())
      .map((weightType: any) => {
        sheet.insertRule(
          `:root {--font-${rule}-${weightType}-weight: ${weightType}}`
        );
      });
  }

  private injectFallbackFonts(sheet: CSSStyleSheet) {
    sheet.insertRule(`:root {--font-body-normal-weight: normal}`);
    sheet.insertRule(`:root {--font-body-bold-weight: bold}`);
    sheet.insertRule(`:root {font-size: 32px !important;}`);
    sheet.insertRule(`ion-app {font-size:32px !important;}`);
  }
}
